import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import "./EmployeeLogin.css";
import API_BASE_URL from "../config";

const EmployeeLogin = () => {
  const [empId, setEmpId] = useState("");
  const [companyEmail, setCompanyEmail] = useState("");
  const [otp, setOtp] = useState("");
  const [otpSent, setOtpSent] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [employeeName, setEmployeeName] = useState("");
  const [otpTimer, setOtpTimer] = useState(0);
  const navigate = useNavigate();

  // Timer for OTP expiry
  useEffect(() => {
    let interval;
    if (otpTimer > 0) {
      interval = setInterval(() => {
        setOtpTimer((prev) => prev - 1);
      }, 1000);
    }
    return () => clearInterval(interval);
  }, [otpTimer]);

  // Format timer display
  const formatTimer = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs < 10 ? '0' : ''}${secs}`;
  };

  const clearMessages = () => {
    setError("");
    setSuccess("");
  };

  const handleSendOTP = async (e) => {
    e.preventDefault();
    clearMessages();
    
    if (!empId || !companyEmail) {
      setError("Please enter both Employee ID and Company Email");
      return;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(companyEmail)) {
      setError("Please enter a valid email address");
      return;
    }

    setIsSubmitting(true);
    try {
      const res = await fetch(`${API_BASE_URL}/api/employees/login/initiate`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ 
          emp_id: empId.trim(), 
          company_email: companyEmail.trim().toLowerCase() 
        }),
      });

      const data = await res.json();
      if (res.status === 200 && data.success) {
        setOtpSent(true);
        setEmployeeName(data.name || "");
        setSuccess(`Verification code sent to ${companyEmail}`);
        setOtpTimer(600); // 10 minutes
        setError("");
      } else {
        setError(data.message || "Failed to send OTP");
      }
    } catch (err) {
      console.error("Error sending OTP:", err);
      setError("Server error. Please try again later.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleVerifyOTP = async (e) => {
    e.preventDefault();
    clearMessages();
    
    if (!otp || otp.length !== 6) {
      setError("Please enter a valid 6-digit OTP");
      return;
    }

    setIsSubmitting(true);
    try {
      const res = await fetch(`${API_BASE_URL}/api/employees/login/verify`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ 
          emp_id: empId.trim(), 
          otp: otp.trim() 
        }),
      });

      const data = await res.json();
      if (res.status === 200 && data.success) {
        setSuccess("Login successful!");
        setError("");
        
        sessionStorage.setItem("employee", JSON.stringify(data.employee));
        sessionStorage.setItem("emp_token", data.token);
        sessionStorage.setItem("empId", empId);
        sessionStorage.setItem("empEmail", companyEmail);
        
        navigate("/employeedashboard");
      } else {
        setError(data.message || "Verification failed");
      }
    } catch (err) {
      console.error("Error verifying OTP:", err);
      setError("Server error. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleResendOTP = async () => {
    clearMessages();
    setIsSubmitting(true);

    try {
      const res = await fetch(`${API_BASE_URL}/api/employees/login/resend-otp`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ 
          emp_id: empId.trim(), 
          company_email: companyEmail.trim().toLowerCase() 
        }),
      });

      const data = await res.json();
      if (res.status === 200 && data.success) {
        setSuccess("New OTP sent to your company email");
        setOtpTimer(600);
        setOtp("");
      } else {
        setError(data.message || "Failed to resend OTP");
      }
    } catch (err) {
      console.error(err);
      setError("Failed to resend OTP. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleBack = () => {
    setOtpSent(false);
    setOtp("");
    setEmployeeName("");
    setOtpTimer(0);
    clearMessages();
  };

  const handleOtpChange = (e) => {
    const value = e.target.value.replace(/\D/g, '').slice(0, 6);
    setOtp(value);
    clearMessages();
  };

  return (
    <div className="emp-auth-wrapper">
      {/* Background */}
      <div className="emp-bg-layer">
        <div className="emp-bg-gradient"></div>
      </div>

      {/* Main Content Grid */}
      <div className="emp-content-grid">
        {/* Right Panel - Auth Form */}
        <div className="emp-form-panel">
          <div className="emp-form-container">
            {!otpSent ? (
              // Step 1: Credentials Entry
              <div className="emp-form-card">
                <div className="hrcp-logo-container">
                <div className="quantum-nav__logo">
                  <div className="quantum-nav__logo-orb">
                    <img 
                      src="/images/logo415.png" 
                      alt="Logo" 
                      className="quantum-nav__logo-img" 
                    />
                    <span className="Humanex_logo_title">
                      umanex
                    </span>
                  </div>
                </div>
              </div>
                <div className="emp-form-header">
                  <h2 className="emp-form-title">Welcome Back</h2>
                  <p className="emp-form-subtitle">Sign in to access your employee dashboard</p>
                </div>

                {error && (
                  <div className="emp-alert emp-alert-error">
                    <span className="emp-alert-icon">⚠️</span>
                    <span className="emp-alert-text">{error}</span>
                  </div>
                )}

                {success && (
                  <div className="emp-alert emp-alert-success">
                    <span className="emp-alert-icon">✓</span>
                    <span className="emp-alert-text">{success}</span>
                  </div>
                )}

                <form className="emp-auth-form" onSubmit={handleSendOTP}>
                  <div className="emp-input-group">
                    <label className="emp-input-label">
                      <span className="emp-label-text">Employee ID</span>
                      <span className="emp-label-required">*</span>
                    </label>
                    <div className="emp-input-wrapper">
                      <span className="emp-input-icon"></span>
                      <input
                        type="text"
                        className="emp-input-field"
                        value={empId}
                        onChange={(e) => {
                          setEmpId(e.target.value);
                          clearMessages();
                        }}
                        placeholder="Enter your Employee ID"
                        required
                        disabled={isSubmitting}
                      />
                    </div>
                  </div>

                  <div className="emp-input-group">
                    <label className="emp-input-label">
                      <span className="emp-label-text">Company Email</span>
                      <span className="emp-label-required">*</span>
                    </label>
                    <div className="emp-input-wrapper">
                      <span className="emp-input-icon"></span>
                      <input
                        type="email"
                        className="emp-input-field"
                        value={companyEmail}
                        onChange={(e) => {
                          setCompanyEmail(e.target.value);
                          clearMessages();
                        }}
                        placeholder="name@company.com"
                        required
                        disabled={isSubmitting}
                      />
                    </div>
                  </div>

                  <button 
                    type="submit" 
                    className="emp-btn emp-btn-primary"
                    disabled={isSubmitting}
                  >
                    {isSubmitting ? (
                      <>
                        <span className="emp-spinner"></span>
                        <span>Sending...</span>
                      </>
                    ) : (
                      <>
                        <span>Send Verification Code</span>
                        <span className="emp-btn-arrow">→</span>
                      </>
                    )}
                  </button>

                  <div className="emp-security-note">
                    <span className="emp-security-icon">🔒</span>
                    <span className="emp-security-text">Use your company email for secure access</span>
                  </div>
                </form>
              </div>
            ) : (
              // Step 2: OTP Verification
              <div className="emp-form-card">
                <div className="emp-form-header">
                  <button 
                    className="emp-back-btn"
                    onClick={handleBack}
                    type="button"
                  >
                    ← Back
                  </button>
                  <h2 className="emp-form-title">Verify Your Email</h2>
                  <p className="emp-form-subtitle">
                    {employeeName && <span>Hello, <strong>{employeeName}</strong><br/></span>}
                    Code sent to <strong className="emp-email-highlight">{companyEmail}</strong>
                  </p>
                </div>

                {error && (
                  <div className="emp-alert emp-alert-error">
                    <span className="emp-alert-icon">⚠️</span>
                    <span className="emp-alert-text">{error}</span>
                  </div>
                )}

                {success && (
                  <div className="emp-alert emp-alert-success">
                    <span className="emp-alert-icon">✓</span>
                    <span className="emp-alert-text">{success}</span>
                  </div>
                )}

                <form className="emp-auth-form" onSubmit={handleVerifyOTP}>
                  <div className="emp-input-group">
                    <label className="emp-input-label">
                      <span className="emp-label-text">Verification Code</span>
                    </label>
                    <input
                      type="text"
                      className="emp-input-field emp-otp-input"
                      value={otp}
                      onChange={handleOtpChange}
                      placeholder="000000"
                      maxLength={6}
                      required
                      disabled={isSubmitting}
                    />
                    <p className="emp-input-hint">
                      Enter the 6-digit code • Expires in {formatTimer(otpTimer)}
                    </p>
                  </div>

                  <button 
                    type="submit" 
                    className="emp-btn emp-btn-primary"
                    disabled={isSubmitting || otp.length !== 6}
                  >
                    {isSubmitting ? (
                      <>
                        <span className="emp-spinner"></span>
                        <span>Verifying...</span>
                      </>
                    ) : (
                      <>
                        <span>Verify & Access Dashboard</span>
                        <span className="emp-btn-arrow">→</span>
                      </>
                    )}
                  </button>

                  <button 
                    type="button"
                    className="emp-btn emp-btn-secondary"
                    onClick={handleResendOTP}
                    disabled={isSubmitting || otpTimer > 540}
                  >
                    {otpTimer > 540 ? `Resend in ${formatTimer(otpTimer - 540)}` : 'Resend Code'}
                  </button>
                </form>
              </div>
            )}

            {/* Footer */}
            {/* <div className="emp-form-footer">
              <p className="emp-footer-text">
                <span className="emp-footer-icon">🔐</span>
                Your data is protected with enterprise-grade security
              </p>
            </div> */}
          </div>
        </div>
      </div>
    </div>
  );
};

export default EmployeeLogin;